// Contractor template service trade data — buyer customizes copy/trades.
import {
  Wrench, Droplets, Zap, Home, Hammer, Trees,
  type LucideIcon,
} from "lucide-react";

export type TradeService = {
  title: string;
  description: string;
};

export type TradeFAQ = {
  q: string;
  a: string;
};

export type TradePage = {
  slug: string;          // URL slug (used as /services/:slug)
  name: string;          // Short menu label e.g. "HVAC"
  title: string;         // SEO/h1 e.g. "HVAC Services"
  tagline: string;       // Hero subtitle
  heroLine: string;      // Hero one-liner
  icon: LucideIcon;
  problems: string[];    // "Problems we solve"
  services: TradeService[];
  whyUs: string[];
  faqs: TradeFAQ[];
  metaDescription: string;
};

export const TRADES: TradePage[] = [
  {
    slug: "hvac",
    name: "HVAC",
    title: "HVAC Services",
    tagline: "Heating, cooling, and indoor air done right.",
    heroLine:
      "Fast, honest HVAC repair, install, and maintenance — same-day service from licensed technicians.",
    icon: Wrench,
    problems: [
      "AC not cooling or blowing warm air",
      "Furnace won't ignite or short-cycles",
      "High energy bills and uneven temperatures",
      "Aging system past its useful life (10–15+ years)",
      "Poor indoor air quality, dust, or humidity issues",
    ],
    services: [
      { title: "AC Repair & Replacement", description: "Diagnostics, refrigerant recharge, compressor and full system replacement." },
      { title: "Furnace & Heating", description: "Gas, electric, and heat pump repair, tune-ups, and new installs." },
      { title: "Mini-Split & Ductless", description: "Single and multi-zone ductless systems for additions, garages, and retrofits." },
      { title: "Indoor Air Quality", description: "Filtration, UV lights, humidifiers, and duct cleaning." },
      { title: "Maintenance Plans", description: "Twice-yearly tune-ups, priority service, and discounted repairs." },
    ],
    whyUs: [
      "Upfront flat-rate pricing — no surprises",
      "Licensed, insured, background-checked technicians",
      "Same-day and emergency service available",
      "10-year parts & labor warranty on new installs",
      "Financing with approved credit",
    ],
    faqs: [
      { q: "How much does a new AC system cost?", a: "Most full system replacements run $5,500–$12,000 installed depending on tonnage, SEER, and ductwork. Use our instant photo quote for a same-day estimate." },
      { q: "How often should I service my HVAC?", a: "Twice a year — once in spring for cooling and once in fall for heating. Annual maintenance is the cheapest way to extend system life." },
      { q: "Do you offer financing?", a: "Yes. We offer 0% promotional financing and longer-term plans through approved lenders." },
    ],
    metaDescription:
      "Trusted local HVAC company. AC, furnace, heat pump, and mini-split installation, repair, and maintenance. Upfront pricing, same-day service, financing available.",
  },
  {
    slug: "plumbing",
    name: "Plumbing",
    title: "Plumbing Services",
    tagline: "Leaks, drains, water heaters — fixed right the first time.",
    heroLine:
      "Licensed plumbers handling everything from clogged drains to full repipes. Upfront pricing, 24/7 emergency service.",
    icon: Droplets,
    problems: [
      "Leaks, drips, and water damage",
      "Clogged or slow drains and sewer backups",
      "No hot water or failing water heater",
      "Low water pressure throughout the home",
      "Old galvanized or polybutylene pipes",
    ],
    services: [
      { title: "Drain Cleaning & Hydro Jetting", description: "Clear stubborn clogs in sinks, tubs, and main sewer lines." },
      { title: "Water Heater Service", description: "Tank, tankless, and hybrid water heater repair and replacement." },
      { title: "Leak Detection & Repair", description: "Slab leaks, hidden leaks, and emergency shut-off." },
      { title: "Repiping & Fixture Install", description: "PEX/copper repipes, faucets, toilets, garbage disposals." },
      { title: "Sewer Line Repair", description: "Camera inspection, trenchless repair, full replacement." },
    ],
    whyUs: [
      "24/7 emergency service",
      "Licensed master plumbers on every job",
      "Flat-rate pricing before work begins",
      "Lifetime warranty on most repipes",
      "Senior, military, and first-responder discounts",
    ],
    faqs: [
      { q: "How much does it cost to clear a drain?", a: "Standard drain cleaning starts around $149. Mainline jetting and camera inspection are quoted upfront after diagnostic." },
      { q: "Tankless vs tank water heater — which is better?", a: "Tankless lasts longer (~20 yrs) and gives endless hot water but costs more upfront. We help you compare both with a free in-home quote." },
      { q: "Do you handle insurance claims?", a: "Yes. We document damage with photos and detailed reports for your insurance carrier." },
    ],
    metaDescription:
      "Local licensed plumbers for leaks, water heaters, drains, repipes, and 24/7 emergency plumbing. Upfront pricing, fast response, lifetime warranties.",
  },
  {
    slug: "electrical",
    name: "Electrical",
    title: "Electrical Services",
    tagline: "Safe, code-compliant electrical work.",
    heroLine:
      "Licensed electricians for panel upgrades, EV chargers, lighting, and emergency repairs. Always pulled, always inspected.",
    icon: Zap,
    problems: [
      "Breakers tripping or panel making noise",
      "Flickering lights or burning smell from outlets",
      "Need EV charger installed (Tesla, J1772, NEMA 14-50)",
      "Old aluminum wiring or knob-and-tube",
      "Adding a circuit, sub-panel, or generator",
    ],
    services: [
      { title: "Panel Upgrades", description: "100A → 200A service upgrades, sub-panels, meter mains." },
      { title: "EV Charger Installation", description: "Level 2 home chargers wired to code with permits and inspection." },
      { title: "Whole-Home Rewire", description: "Replace aluminum or cloth-insulated wiring with modern copper." },
      { title: "Lighting & Outlets", description: "Recessed lights, ceiling fans, GFCI outlets, smart switches." },
      { title: "Generator & Backup Power", description: "Whole-home standby generators and battery backup install." },
    ],
    whyUs: [
      "All work permitted and inspected",
      "Licensed master electricians",
      "Free safety inspection with any service",
      "10-year workmanship warranty",
      "Upfront pricing — no hourly surprises",
    ],
    faqs: [
      { q: "How much is a panel upgrade?", a: "A 200A panel upgrade typically runs $2,500–$4,500 including permits and utility coordination." },
      { q: "Do I need a permit for an EV charger?", a: "Yes. We pull the permit and handle the inspection — most jobs are done in 4–6 hours." },
      { q: "Why do my breakers keep tripping?", a: "Common causes: overloaded circuit, short, ground fault, or a failing breaker. We diagnose for a flat fee and apply it to the repair." },
    ],
    metaDescription:
      "Licensed local electricians. Panel upgrades, EV charger install, rewires, lighting, and emergency electrical service. Permitted, inspected, warrantied.",
  },
  {
    slug: "roofing",
    name: "Roofing",
    title: "Roofing Services",
    tagline: "Repair, replacement, and storm response.",
    heroLine:
      "Local roofing contractor specializing in asphalt, metal, and tile. Free inspections, insurance-claim experts.",
    icon: Home,
    problems: [
      "Roof leak or stained ceiling",
      "Storm, hail, or wind damage",
      "Missing or curling shingles",
      "Aging roof past its warranty (15–20+ years)",
      "Sagging deck or visible rot in fascia",
    ],
    services: [
      { title: "Roof Replacement", description: "Asphalt shingle, metal, tile, and flat roof systems." },
      { title: "Roof Repair", description: "Leaks, flashing, valleys, and emergency tarp service." },
      { title: "Free Inspections", description: "Drone and walk-through inspections with photo report." },
      { title: "Storm & Insurance Claims", description: "We meet the adjuster and document everything." },
      { title: "Gutters & Attic Ventilation", description: "Gutter replacement, gutter guards, ridge vent install." },
    ],
    whyUs: [
      "Manufacturer-certified installers",
      "Lifetime workmanship warranty available",
      "Free drone inspection and report",
      "Insurance claim specialists",
      "Local crews — no out-of-town storm chasers",
    ],
    faqs: [
      { q: "Will my insurance cover a new roof?", a: "If damage is from a covered event (hail, wind, fallen tree), most policies cover full replacement minus deductible. We help document and file." },
      { q: "How long does a roof replacement take?", a: "Most homes are completed in 1–2 days. We protect landscaping and clean up daily." },
      { q: "What's the lifespan of a new roof?", a: "Architectural asphalt: 25–30 years. Metal: 40–60 years. Tile: 50+ years." },
    ],
    metaDescription:
      "Local roofing contractor for repair, replacement, and storm damage. Free drone inspection, insurance claim specialists, lifetime warranty available.",
  },
  {
    slug: "handyman",
    name: "Handyman",
    title: "Handyman Services",
    tagline: "One call for the whole punch list.",
    heroLine:
      "Skilled handymen for the dozens of small jobs every home needs. Half-day, full-day, or fixed-price packages.",
    icon: Hammer,
    problems: [
      "Long honey-do list with nowhere to start",
      "Move-in or move-out repair list",
      "Drywall holes, paint touch-ups, trim repair",
      "Furniture, TV, or shelving needs assembly/mounted",
      "Doors, locks, or hardware that don't work right",
    ],
    services: [
      { title: "Drywall & Paint", description: "Patch, texture-match, and paint repair." },
      { title: "Mounting & Assembly", description: "TV mounts, shelves, IKEA, fitness equipment." },
      { title: "Doors, Locks, Hinges", description: "Sticking doors, deadbolts, smart locks, weatherstripping." },
      { title: "Carpentry & Trim", description: "Baseboards, crown, casing, rotted wood replacement." },
      { title: "Honey-Do Packages", description: "4-hour and 8-hour bundles at a flat price." },
    ],
    whyUs: [
      "Background-checked, uniformed pros",
      "One craftsman, one truck, one call",
      "Flat-rate packages or hourly — your choice",
      "Most jobs done same week",
      "100% satisfaction guarantee",
    ],
    faqs: [
      { q: "Is there a minimum charge?", a: "Our standard minimum is a 2-hour service block. Larger lists fit our 4- and 8-hour discounted packages." },
      { q: "Do you supply materials?", a: "Yes — we can pick up materials and add them to the invoice, or you can have them on site." },
      { q: "What jobs are too small?", a: "Nothing's too small. If it's on your list, it's on ours." },
    ],
    metaDescription:
      "Local handyman service for drywall, paint, mounting, carpentry, and home repair. Flat-rate packages, background-checked pros, satisfaction guarantee.",
  },
  {
    slug: "landscaping",
    name: "Landscaping",
    title: "Landscaping Services",
    tagline: "Curb appeal, lawn care, and outdoor living.",
    heroLine:
      "Full-service landscaping — weekly maintenance, irrigation, hardscape, and seasonal cleanups.",
    icon: Trees,
    problems: [
      "Lawn looks tired, patchy, or weed-filled",
      "Sprinklers leaking, broken, or watering driveway",
      "Overgrown yard after vacation or move-in",
      "Want a patio, walkway, or retaining wall",
      "Fall leaves, spring cleanup, or storm debris",
    ],
    services: [
      { title: "Weekly Lawn Maintenance", description: "Mow, edge, trim, blow — flat monthly pricing." },
      { title: "Irrigation Repair & Install", description: "Heads, valves, controllers, and full system installs." },
      { title: "Hardscape & Patios", description: "Pavers, retaining walls, walkways, fire pits." },
      { title: "Mulch, Sod, & Planting", description: "Seasonal mulch refresh, sod install, tree and shrub planting." },
      { title: "Seasonal Cleanups", description: "Spring prep, fall leaves, storm debris haul-off." },
    ],
    whyUs: [
      "Same crew every visit",
      "Insured and licensed",
      "No long-term contracts on maintenance",
      "Free design consult on hardscape projects",
      "Eco-friendly fertilizer and weed control options",
    ],
    faqs: [
      { q: "How much is weekly lawn service?", a: "Most suburban yards run $40–$80/visit billed monthly. Get an instant quote with a few photos of your yard." },
      { q: "Do you do design work?", a: "Yes. For hardscape and full landscape projects we provide free 3D design consultations." },
      { q: "Can you fix my sprinkler system?", a: "Absolutely — heads, valves, broken lines, and smart controllers. Most repairs done same visit." },
    ],
    metaDescription:
      "Local landscaping company for weekly lawn care, irrigation repair, hardscape, and seasonal cleanups. Insured crews, no long-term contracts, free design.",
  },
];

export const getTrade = (slug: string) => TRADES.find((t) => t.slug === slug);
